import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Box, Paper, Typography } from '@mui/material';
import { ShipBobPerformanceDailyRow } from 'models/ShipBobPerformanceModel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface DailyTrendChartProps {
  daily: ShipBobPerformanceDailyRow[];
}

export default function DailyTrendChart({ daily }: DailyTrendChartProps) {
  const labels = daily.map(d => d.date);

  const data = {
    labels,
    datasets: [
      {
        type: 'line' as const,
        label: 'On-time %',
        data: daily.map(d => d.onTimePct),
        borderColor: '#15803D',
        backgroundColor: '#15803D',
        yAxisID: 'pct',
        tension: 0.3,
        pointRadius: 2,
      },
      {
        type: 'bar' as const,
        label: 'Orders',
        data: daily.map(d => d.orders),
        backgroundColor: '#4F46E5',
        borderRadius: 2,
        yAxisID: 'count',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // A Dashboard-page chart registers chartjs-plugin-datalabels globally;
      // keep point labels off here.
      datalabels: { display: false },
      legend: {
        position: 'top' as const,
        labels: { color: '#334155', font: { size: 12 } },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            if (context.dataset.label === 'On-time %') {
              return `On-time: ${context.parsed.y?.toFixed(1)}%`;
            }
            return `Orders: ${context.parsed.y?.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#334155', font: { size: 11 } },
        grid: { display: false },
      },
      count: {
        position: 'left' as const,
        ticks: { color: '#334155' },
        grid: { color: '#E2E8F0' },
        title: { display: true, text: 'Orders', color: '#64748B' },
      },
      pct: {
        position: 'right' as const,
        min: 0,
        max: 100,
        ticks: { color: '#15803D' },
        grid: { display: false },
        title: { display: true, text: 'On-time %', color: '#15803D' },
      },
    },
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Daily orders & on-time ship rate
      </Typography>
      <Box sx={{ height: 320 }}>
        <Chart type="bar" data={data} options={options} />
      </Box>
    </Paper>
  );
}
